import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, Plus, Zap, Edit2, Trash2, ArrowUp, ArrowDown, TestTube2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import ForwarderEditor from '../components/forms/ForwarderEditor';
import { getAllFieldsFromSteps } from '../lib/formUtils';
import { maskKey } from '../lib/mask';
import { isStoredCredential } from '../lib/forwarderCredentials';
import { isOrgAdminUser } from '../utils/orgRoles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Helpers ─────────────────────────────────────────────────────────────

/** Human-readable label for the forwarder type. */
function typeLabel(type) {
    if (type === 'gohighlevel') return 'GoHighLevel · Native';
    return 'Custom API';
}

/** Human-readable label for auth type. */
function authLabel(type) {
    const map = {
        none: 'None',
        bearer: 'Bearer Token',
        api_key: 'API Key Header',
        basic: 'Basic Auth',
        custom_headers: 'Custom Headers',
    };
    return map[type] || type || 'None';
}

/** Boolean → Enabled / Disabled label. */
function boolLabel(val) {
    return val ? 'Enabled' : 'Disabled';
}

// ─── Toggle Switch (controlled React component) ──────────────────────────

function ToggleSwitch({ checked, onChange, disabled }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            disabled={disabled}
            className={`fp-toggle ${checked ? 'fp-toggle-on' : 'fp-toggle-off'}`}
            onClick={() => !disabled && onChange(!checked)}
        >
            <span className={`fp-toggle-knob ${checked ? 'fp-toggle-knob-on' : ''}`} />
        </button>
    );
}

// ─── Config Row ──────────────────────────────────────────────────────────

function ConfigRow({ label, children }) {
    return (
        <div className="fp-config-row">
            <span className="fp-config-label">{label}</span>
            <span className="fp-config-value">{children}</span>
        </div>
    );
}

// ─── Forwarder Card ──────────────────────────────────────────────────────

function ForwarderCard({ forwarder, index, total, onToggle, onEdit, onTest, onMoveUp, onMoveDown, onDelete, testing, testResult, canTest = true }) {
    const isGHL = forwarder.type === 'gohighlevel';
    const iconBg = isGHL ? '#E3F3EA' : '#EFF6FF';
    const iconColor = isGHL ? '#1A7F4E' : '#3B82F6';

    return (
        <div className="fp-card">
            {/* Header row */}
            <div className="fp-card-header">
                <div className="fp-card-icon" style={{ background: iconBg, color: iconColor }}>
                    <Zap size={20} />
                </div>
                <div className="fp-card-titles">
                    <div className="fp-card-name">{forwarder.name || 'Unnamed Forwarder'}</div>
                    <div className="fp-card-type">{typeLabel(forwarder.type)}</div>
                </div>
                {forwarder.enabled !== false && (
                    <span className="fp-active-badge">Active</span>
                )}
                <ToggleSwitch
                    checked={forwarder.enabled !== false}
                    onChange={(val) => onToggle(index, val)}
                />
            </div>

            {/* Config rows */}
            <div className="fp-config-section">
                {isGHL ? (
                    <>
                        <ConfigRow label="Location ID">
                            <code>{forwarder.ghl_location_id ?? (forwarder.location_id || '—')}</code>
                        </ConfigRow>
                        <ConfigRow label="Private Integration Token">
                            <code>{isStoredCredential(forwarder.ghl_api_key ?? forwarder.api_key)
                                ? 'Saved'
                                : maskKey(forwarder.ghl_api_key ?? forwarder.api_key)}</code>
                        </ConfigRow>
                        <ConfigRow label="Send UTM">
                            {boolLabel(forwarder.send_utm)}
                        </ConfigRow>
                    </>
                ) : (
                    <>
                        <ConfigRow label="Endpoint URL">
                            <code className="fp-url-value">{forwarder.url || '—'}</code>
                        </ConfigRow>
                        <ConfigRow label="HTTP Method">
                            {forwarder.method || 'POST'}
                        </ConfigRow>
                        <ConfigRow label="Auth Type">
                            {authLabel(forwarder.auth?.type)}
                        </ConfigRow>
                        {forwarder.payload_template && (
                            <div className="fp-payload-row">
                                <span className="fp-config-label">Payload Template</span>
                                <pre className="fp-payload-block">{forwarder.payload_template}</pre>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Test result badge */}
            {testResult && (
                <div className={`fp-test-result ${testResult.success ? 'fp-test-ok' : 'fp-test-fail'}`}>
                    {testResult.success
                        ? `✓ ${testResult.message}`
                        : `✕ ${testResult.message}`}
                </div>
            )}

            {/* Action buttons */}
            <div className="fp-card-actions">
                <button className="fp-action-btn" onClick={() => onEdit(index)}>
                    <Edit2 size={13} /> Edit
                </button>
                {canTest && (
                    <button className="fp-action-btn" onClick={() => onTest(index)} disabled={testing}>
                        {testing ? <Loader2 size={13} className="fp-spin" /> : <TestTube2 size={13} />}
                        Test
                    </button>
                )}
                <button
                    className="fp-action-btn fp-action-icon"
                    onClick={() => onMoveUp(index)}
                    disabled={index === 0}
                    title="Move up"
                >
                    <ArrowUp size={14} />
                </button>
                <button
                    className="fp-action-btn fp-action-icon"
                    onClick={() => onMoveDown(index)}
                    disabled={index === total - 1}
                    title="Move down"
                >
                    <ArrowDown size={14} />
                </button>
                <button
                    className="fp-action-btn fp-action-icon fp-action-danger"
                    onClick={() => onDelete(index)}
                    title="Delete forwarder"
                >
                    <Trash2 size={14} />
                </button>
            </div>
        </div>
    );
}

// ─── Main Component ──────────────────────────────────────────────────────

export default function ForwarderPage() {
    const { formId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeader, user } = useAuth();
    // Adding a forwarder and testing one are org-admin actions (PR #740 F6/N2);
    // the server refuses anyone else, so the controls are not offered.
    const isAdmin = isOrgAdminUser(user);

    const [form, setForm] = useState(null);
    const [forwarders, setForwarders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showEditor, setShowEditor] = useState(false);
    const [editingIndex, setEditingIndex] = useState(-1);
    const [testingIndex, setTestingIndex] = useState(null);
    const [testResults, setTestResults] = useState({});
    const [searchParams] = useSearchParams();

    // ── Load form on mount ──────────────────────────────────────────────
    const loadForm = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/forms/${formId}`, { headers: getAuthHeader() });
            setForm(res.data);
            const fwds = res.data.forwarders || [];
            setForwarders(fwds);

            // Auto-open editor for the forwarder matching the ?step= query param
            const stepId = searchParams.get('step');
            if (stepId) {
                const idx = fwds.findIndex(f => f.step_id === stepId);
                if (idx >= 0) {
                    setEditingIndex(idx);
                    setShowEditor(true);
                }
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to load form');
            navigate('/forms');
        } finally {
            setLoading(false);
        }
    }, [formId, getAuthHeader, navigate, searchParams]);

    useEffect(() => {
        loadForm();
    }, [loadForm]);

    // ── Save forwarders to backend ──────────────────────────────────────
    const saveForwarders = useCallback(async (updatedForwarders) => {
        if (!form) return;
        setSaving(true);
        try {
            const payload = { ...form, forwarders: updatedForwarders };
            const res = await axios.put(`${API}/forms/${formId}`, payload, { headers: getAuthHeader() });
            setForm(res.data);
            setForwarders(res.data.forwarders || updatedForwarders);
        } catch (err) {
            // e.g. a 422 for a new destination that failed verification: drop the
            // optimistic update so the list shows what the server actually holds.
            setForwarders(form.forwarders || []);
            toast.error(err.response?.data?.detail || 'Failed to save forwarders');
        } finally {
            setSaving(false);
        }
    }, [form, formId, getAuthHeader]);

    // ── Toggle forwarder enabled state ──────────────────────────────────
    const handleToggle = useCallback((index, enabled) => {
        const updated = forwarders.map((f, i) => i === index ? { ...f, enabled } : f);
        setForwarders(updated);
        saveForwarders(updated);
    }, [forwarders, saveForwarders]);

    // ── Open editor for add or edit ─────────────────────────────────────
    const handleAdd = () => {
        setEditingIndex(-1);
        setShowEditor(true);
    };

    const handleEdit = (index) => {
        setEditingIndex(index);
        setShowEditor(true);
    };

    // ── Save from editor ────────────────────────────────────────────────
    const handleSaveFromEditor = (config) => {
        let updated;
        if (editingIndex === -1) {
            updated = [...forwarders, config];
        } else {
            updated = forwarders.map((f, i) => i === editingIndex ? config : f);
        }
        setForwarders(updated);
        setShowEditor(false);
        saveForwarders(updated);
        toast.success(editingIndex === -1 ? 'Forwarder added' : 'Forwarder updated');
    };

    // ── Delete forwarder ────────────────────────────────────────────────
    const handleDelete = useCallback((index) => {
        const fwd = forwarders[index];
        if (!window.confirm(`Delete "${fwd.name || 'this forwarder'}"? This cannot be undone.`)) return;
        const updated = forwarders.filter((_, i) => i !== index);
        setForwarders(updated);
        // Clear test result for deleted index
        setTestResults(prev => {
            const next = {};
            Object.entries(prev).forEach(([k, v]) => {
                const ki = parseInt(k);
                if (ki < index) next[ki] = v;
                else if (ki > index) next[ki - 1] = v;
            });
            return next;
        });
        saveForwarders(updated);
        toast.success('Forwarder deleted');
    }, [forwarders, saveForwarders]);

    // ── Reorder forwarder ───────────────────────────────────────────────
    const handleMoveUp = useCallback((index) => {
        if (index === 0) return;
        const updated = [...forwarders];
        [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
        setForwarders(updated);
        saveForwarders(updated);
    }, [forwarders, saveForwarders]);

    const handleMoveDown = useCallback((index) => {
        if (index === forwarders.length - 1) return;
        const updated = [...forwarders];
        [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
        setForwarders(updated);
        saveForwarders(updated);
    }, [forwarders, saveForwarders]);

    // ── Test forwarder ─────────────────────────────────────────────────
    const handleTest = useCallback(async (index) => {
        setTestingIndex(index);
        try {
            const res = await axios.post(
                `${API}/forms/forwarders/test`,
                { config: forwarders[index], sample_data: null, form_id: formId },
                { headers: getAuthHeader() }
            );
            if (res.data.success) {
                const timeMs = res.data.latency_ms ?? 0;
                setTestResults(prev => ({
                    ...prev,
                    [index]: { success: true, message: `200 OK (${timeMs}ms)` },
                }));
                toast.success('Test successful');
            } else {
                setTestResults(prev => ({
                    ...prev,
                    [index]: { success: false, message: res.data.error || 'Delivery failed' },
                }));
                toast.error('Test failed');
            }
        } catch (err) {
            const msg = err.response?.data?.detail || err.response?.data?.message || 'Connection failed';
            setTestResults(prev => ({
                ...prev,
                [index]: { success: false, message: msg },
            }));
            toast.error('Test failed');
        } finally {
            setTestingIndex(null);
        }
    }, [forwarders, getAuthHeader, formId]);

    // ── Gather form fields for the editor's variable insertion ──────────
    const formFields = form?.steps ? getAllFieldsFromSteps(form.steps) : (form?.fields || []);

    // ── Loading state ───────────────────────────────────────────────────
    if (loading) {
        return (
            <>
                <style>{FP_STYLES}</style>
                <div className="fp-page">
                    <div className="fp-header">
                        <div className="fp-header-left">
                            <button className="fp-back-btn" onClick={() => navigate(`/forms/${formId}/workflow`)}>
                                <ChevronLeft size={18} />
                            </button>
                            <h1 className="fp-page-title">
                            {searchParams.get('step')
                                ? `Forwarders — ${(form?.steps?.find(s => s.step_id === searchParams.get('step')) || form?.forwarders?.find(f => f.step_id === searchParams.get('step')))?.title || form?.forwarders?.find(f => f.step_id === searchParams.get('step'))?.name || 'Node'}`
                                : 'Data Forwarders'}
                        </h1>
                        </div>
                    </div>
                    <div className="fp-loading">
                        <Loader2 size={24} className="fp-spin" />
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <style>{FP_STYLES}</style>
            <div className="fp-page">
                {/* Header bar */}
                <div className="fp-header">
                    <div className="fp-header-left">
                        <button
                            className="fp-back-btn"
                            onClick={() => navigate(`/forms/${formId}/workflow`)}
                            title="Back to workflow"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <h1 className="fp-page-title">
                            {searchParams.get('step')
                                ? `Forwarders — ${(form?.steps?.find(s => s.step_id === searchParams.get('step')) || form?.forwarders?.find(f => f.step_id === searchParams.get('step')))?.title || form?.forwarders?.find(f => f.step_id === searchParams.get('step'))?.name || 'Node'}`
                                : 'Data Forwarders'}
                        </h1>
                        {saving && <span className="fp-saving-label">Saving…</span>}
                    </div>
                    {isAdmin && (
                        <button className="fp-add-btn" onClick={handleAdd}>
                            <Plus size={15} /> Add Forwarder
                        </button>
                    )}
                </div>

                {/* Content area */}
                <div className="fp-content">
                    {(() => {
                        // When coming from "Edit Node" (?step=), filter to only that node's forwarders
                        const filterStep = searchParams.get('step');
                        const visibleForwarders = filterStep
                            ? forwarders.filter(f => f.step_id === filterStep)
                            : forwarders;
                        if (visibleForwarders.length === 0) {
                            return (
                                <div className="fp-empty">
                                    <div className="fp-empty-icon">
                                        <Zap size={28} />
                                    </div>
                                    <p className="fp-empty-text">
                                        {filterStep
                                            ? 'No forwarders on this node yet'
                                            : 'No forwarders configured yet'}
                                    </p>
                                    {isAdmin && (
                                        <button className="fp-add-btn" onClick={handleAdd}>
                                            <Plus size={15} /> Add Forwarder
                                        </button>
                                    )}
                                </div>
                            );
                        }
                        return (
                            <div className="fp-card-list">
                                {visibleForwarders.map((fwd) => {
                                    const idx = forwarders.indexOf(fwd);
                                    return (
                                        <ForwarderCard
                                            key={idx}
                                            forwarder={fwd}
                                            index={idx}
                                            total={visibleForwarders.length}
                                            onToggle={handleToggle}
                                            onEdit={handleEdit}
                                            onTest={handleTest}
                                            canTest={isAdmin}
                                            onMoveUp={handleMoveUp}
                                            onMoveDown={handleMoveDown}
                                            onDelete={handleDelete}
                                            testing={testingIndex === idx}
                                            testResult={testResults[idx]}
                                        />
                                    );
                                })}
                            </div>
                        );
                    })()}
                </div>
            </div>

            {/* Forwarder Editor Modal */}
            <ForwarderEditor
                open={showEditor}
                onClose={() => setShowEditor(false)}
                onSave={handleSaveFromEditor}
                forwarder={editingIndex >= 0 ? forwarders[editingIndex] : null}
                formFields={formFields}
                allForwarders={forwarders}
                filterStepId={searchParams.get('step')}
                formId={formId}
            />
        </>
    );
}

// ─── Styles ──────────────────────────────────────────────────────────────

const FP_STYLES = `
.fp-page {
    /* Fills the shell's full-height flex column for this route — see RuntimeShell. */
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    background: #F7F6F2;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* Header */
.fp-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 24px;
    background: #FFFFFF;
    border-bottom: 1px solid #E8E5DF;
    flex-shrink: 0;
}
.fp-header-left {
    display: flex;
    align-items: center;
    gap: 12px;
}
.fp-back-btn {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    border: 1px solid #E8E5DF;
    background: #FFFFFF;
    color: #57534E;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.15s;
}
.fp-back-btn:hover {
    background: #F1F0EB;
}
.fp-page-title {
    font-size: 18px;
    font-weight: 700;
    color: #1C1917;
    margin: 0;
}
.fp-saving-label {
    font-size: 12px;
    color: #6E6961;
    font-weight: 400;
}
.fp-add-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border-radius: 6px;
    background: #1C1917;
    color: #F7F6F2;
    font-size: 13px;
    font-weight: 500;
    border: none;
    cursor: pointer;
    transition: background 0.15s;
}
.fp-add-btn:hover {
    background: #292524;
}

/* Content */
.fp-content {
    flex: 1;
    overflow-y: auto;
    padding: 24px 16px 48px;
    display: flex;
    justify-content: center;
}
.fp-card-list {
    width: 100%;
    max-width: 720px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

/* Card */
.fp-card {
    background: #FFFFFF;
    border: 1px solid #E8E5DF;
    border-radius: 10px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.04);
    padding: 16px 18px;
}

/* Card header */
.fp-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
}
.fp-card-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
.fp-card-titles {
    flex: 1;
    min-width: 0;
}
.fp-card-name {
    font-size: 15px;
    font-weight: 700;
    color: #1C1917;
    line-height: 1.3;
}
.fp-card-type {
    font-size: 12px;
    color: #6E6961;
    margin-top: 1px;
}
.fp-active-badge {
    font-size: 11px;
    font-weight: 600;
    color: #1A7F4E;
    background: #E3F3EA;
    padding: 3px 8px;
    border-radius: 999px;
    flex-shrink: 0;
}

/* Toggle switch */
.fp-toggle {
    width: 40px;
    height: 22px;
    border-radius: 999px;
    border: none;
    padding: 0;
    cursor: pointer;
    position: relative;
    flex-shrink: 0;
    transition: background 0.2s ease;
}
.fp-toggle:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
.fp-toggle-on {
    background: #1A7F4E;
}
.fp-toggle-off {
    background: #D6D3D1;
}
.fp-toggle-knob {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #FFFFFF;
    box-shadow: 0 1px 2px rgba(0,0,0,0.2);
    transition: transform 0.2s ease;
}
.fp-toggle-knob-on {
    transform: translateX(18px);
}

/* Config section */
.fp-config-section {
    border-top: 1px solid #F1F0EB;
    padding-top: 12px;
}
.fp-config-row {
    display: flex;
    align-items: baseline;
    gap: 12px;
    padding: 5px 0;
}
.fp-config-label {
    width: 140px;
    flex-shrink: 0;
    font-size: 12px;
    font-weight: 700;
    color: #6E6961;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}
.fp-config-value {
    font-size: 13px;
    color: #1C1917;
    font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
    word-break: break-all;
    min-width: 0;
}
.fp-url-value {
    font-size: 12px;
    color: #3B82F6;
}

/* Payload template block */
.fp-payload-row {
    padding: 8px 0 4px;
}
.fp-payload-block {
    margin-top: 4px;
    background: #1C1917;
    color: #F7F6F2;
    padding: 10px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
    overflow-x: auto;
    white-space: pre-wrap;
    line-height: 1.5;
    max-height: 160px;
}

/* Test result badge */
.fp-test-result {
    margin-top: 10px;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    display: inline-block;
}
.fp-test-ok {
    background: #E3F3EA;
    color: #1A7F4E;
}
.fp-test-fail {
    background: #FBE7E8;
    color: #C2363F;
}

/* Action buttons */
.fp-card-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px solid #F1F0EB;
}
.fp-action-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 5px 10px;
    border-radius: 6px;
    border: 1px solid #E8E5DF;
    background: #FFFFFF;
    color: #57534E;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
}
.fp-action-btn:hover:not(:disabled) {
    background: #F1F0EB;
    border-color: #D6D3D1;
}
.fp-action-btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
}
.fp-action-icon {
    padding: 5px 7px;
}
.fp-action-danger:hover:not(:disabled) {
    color: #C2363F;
    border-color: #FBE7E8;
    background: #FDF2F3;
}

/* Empty state */
.fp-empty {
    text-align: center;
    padding: 60px 20px;
    max-width: 720px;
    width: 100%;
}
.fp-empty-icon {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: #F1F0EB;
    color: #A29D96;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
}
.fp-empty-text {
    font-size: 14px;
    color: #6E6961;
    margin: 0 0 20px;
}

/* Loading */
.fp-loading {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #A29D96;
}

/* Spin animation */
.fp-spin {
    animation: fp-spin 0.8s linear infinite;
}
@keyframes fp-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
`;
